import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';

const TRACKER_TYPES = [
    { value: 'NUMERIC', label: 'Numeric' },
    { value: 'BOOLEAN', label: 'Boolean' },
];

const SCHEDULE_TYPES = [
    { value: 'DAILY', label: 'Daily' },
    { value: 'WEEKLY', label: 'Weekly' },
    { value: 'MONTHLY', label: 'Monthly' },
];

const inputClass = 'bg-[#2A2B32] text-gray-200 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-indigo-500';
const labelClass = 'text-gray-400 text-xs font-medium uppercase tracking-wide mb-1';

export default function AddTrackerModal({
    onClose,
    onSubmit,
    initialData = null,
    isEditMode = false,
    isSubmitting = false,
}) {
    const [form, setForm] = useState({
        title: '',
        description: '',
        type: 'NUMERIC',
        targetValue: '',
        targetUnit: '',
        scheduleType: 'DAILY',
        score: '',
    });

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') onClose();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    useEffect(() => {
        setForm({
            title: initialData?.title ?? initialData?.name ?? '',
            description: initialData?.description ?? '',
            type: initialData?.type ?? initialData?.trackerType ?? 'NUMERIC',
            targetValue: initialData?.targetValue ?? initialData?.numericTarget ?? '',
            targetUnit: initialData?.targetUnit ?? initialData?.target_unit ?? initialData?.unit ?? '',
            scheduleType: initialData?.scheduleType ?? initialData?.schedule_type ?? 'DAILY',
            score: initialData?.score ?? '',
        });
    }, [initialData]);

    const canSave = useMemo(() => {
        if (!form.title.trim()) return false;
        if (form.type !== 'NUMERIC') return true;
        return form.targetValue !== '' && !Number.isNaN(Number(form.targetValue));
    }, [form.title, form.targetValue, form.type]);

    const handleBackdropClick = (event) => {
        if (event.target === event.currentTarget) onClose();
    };

    const handleSubmit = () => {
        onSubmit({
            title: form.title.trim(),
            description: form.description.trim(),
            type: form.type,
            targetValue: form.type === 'NUMERIC' ? Number(form.targetValue) : null,
            targetUnit: form.type === 'NUMERIC' ? form.targetUnit.trim() : '',
            scheduleType: form.scheduleType,
            ...(isEditMode ? { score: form.score === '' ? null : Number(form.score) } : {}),
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm" onClick={handleBackdropClick}>
            <div className="relative w-full max-w-lg rounded-2xl border border-[#2A2B32] bg-[#1E1F25] p-6 shadow-xl">
                <button
                    type="button"
                    onClick={onClose}
                    disabled={isSubmitting}
                    className="absolute right-4 top-4 text-gray-400 transition hover:text-gray-200 disabled:cursor-not-allowed disabled:opacity-60"
                    aria-label="Close tracker modal"
                >
                    <X size={20} />
                </button>

                <h2 className="mb-5 text-xl font-semibold text-gray-100">
                    {isEditMode ? 'Edit Tracker' : 'New Tracker'}
                </h2>

                <div className="flex max-h-[65vh] flex-col gap-4 overflow-y-auto pr-1">
                    <div className="flex flex-col">
                        <label htmlFor="tracker-title" className={labelClass}>
                            Tracker Title <span className="text-indigo-400">*</span>
                        </label>
                        <input
                            id="tracker-title"
                            type="text"
                            value={form.title}
                            onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                            className={inputClass}
                            placeholder="e.g. Read book"
                        />
                    </div>

                    <div className="flex flex-col">
                        <label htmlFor="tracker-description" className={labelClass}>
                            Description
                        </label>
                        <textarea
                            id="tracker-description"
                            value={form.description}
                            onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                            rows={2}
                            className={`${inputClass} resize-none`}
                            placeholder="Daily reading habit"
                        />
                    </div>

                    <div className="flex flex-col">
                        <label htmlFor="tracker-type" className={labelClass}>
                            Type
                        </label>
                        <select
                            id="tracker-type"
                            value={form.type}
                            onChange={(event) => setForm((prev) => ({ ...prev, type: event.target.value }))}
                            className={`${inputClass} appearance-none`}
                        >
                            {TRACKER_TYPES.map((type) => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex flex-col">
                        <label htmlFor="tracker-schedule" className={labelClass}>
                            Schedule
                        </label>
                        <select
                            id="tracker-schedule"
                            value={form.scheduleType}
                            onChange={(event) => setForm((prev) => ({ ...prev, scheduleType: event.target.value }))}
                            className={`${inputClass} appearance-none`}
                        >
                            {SCHEDULE_TYPES.map((schedule) => (
                                <option key={schedule.value} value={schedule.value}>
                                    {schedule.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {isEditMode && (
                        <div className="flex flex-col">
                            <label htmlFor="tracker-score" className={labelClass}>
                                Score (optional)
                            </label>
                            <input
                                id="tracker-score"
                                type="number"
                                value={form.score}
                                onChange={(event) => setForm((prev) => ({ ...prev, score: event.target.value }))}
                                className={inputClass}
                                placeholder="e.g. 80"
                            />
                        </div>
                    )}

                    {form.type === 'NUMERIC' && (
                        <div className="grid grid-cols-2 gap-3">
                            <div className="flex flex-col">
                                <label htmlFor="tracker-target" className={labelClass}>
                                    Success Criteria <span className="text-indigo-400">*</span>
                                </label>
                                <input
                                    id="tracker-target"
                                    type="number"
                                    min="0"
                                    value={form.targetValue}
                                    onChange={(event) => setForm((prev) => ({ ...prev, targetValue: event.target.value }))}
                                    className={inputClass}
                                    placeholder="e.g. 50"
                                />
                            </div>
                            <div className="flex flex-col">
                                <label htmlFor="tracker-unit" className={labelClass}>
                                    Unit
                                </label>
                                <input
                                    id="tracker-unit"
                                    type="text"
                                    value={form.targetUnit}
                                    onChange={(event) => setForm((prev) => ({ ...prev, targetUnit: event.target.value }))}
                                    className={inputClass}
                                    placeholder="e.g. pages"
                                />
                            </div>
                            <p className="col-span-2 mt-1 text-xs text-gray-500">
                                Success is reached when the logged value meets or exceeds this number.
                            </p>
                        </div>
                    )}

                    {form.type === 'BOOLEAN' && (
                        <div className="rounded-xl border border-[#2A2B32] bg-[#171A21] px-4 py-3 text-sm text-gray-400">
                            Boolean trackers use yes/no entry updates instead of a numeric goal.
                        </div>
                    )}
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="rounded-lg border border-gray-600 px-4 py-2 text-gray-300 transition hover:bg-[#2A2B32] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!canSave || isSubmitting}
                        className="rounded-lg bg-indigo-600 px-4 py-2 font-medium text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        {isEditMode ? 'Save Changes' : 'Create Tracker'}
                    </button>
                </div>
            </div>
        </div>
    );
}

AddTrackerModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    initialData: PropTypes.shape({
        title: PropTypes.string,
        name: PropTypes.string,
        description: PropTypes.string,
        type: PropTypes.string,
        trackerType: PropTypes.string,
        targetValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        numericTarget: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        targetUnit: PropTypes.string,
        target_unit: PropTypes.string,
        unit: PropTypes.string,
        scheduleType: PropTypes.string,
        schedule_type: PropTypes.string,
        score: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    isEditMode: PropTypes.bool,
    isSubmitting: PropTypes.bool,
};
